import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { chatsIcon, homeIcon, createIcon, discordIcon } from '../../assets/icons';
import { FiLogIn, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import styles from './NavSidebar.module.css';

interface NavSidebarProps {
  onToggleExpanded?: (expanded: boolean) => void;
}

interface User {
  username: string;
  icon?: string;
}

const NavSidebar: React.FC<NavSidebarProps> = ({ onToggleExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isLoggedIn, user, isLoading } = useAuth();
  const [expanded, setExpanded] = useState<boolean>(false);

  // Function to handle profile click
  const handleProfileClick = (): void => {
    if (isLoggedIn && user) {
      navigate(`/users/${user.username}`);
    } else {
      navigate('/login');
    }
  };

  const toggleExpanded = (): void => {
    const newExpandedState = !expanded;
    setExpanded(newExpandedState);
    if (onToggleExpanded) {
      onToggleExpanded(newExpandedState);
    }
  };

  // Function to handle Discord link click
  const handleDiscordClick = (): void => {
    window.open('https://discord.gg/qN5ugTpfYP', '_blank');
  };

  // Notify parent component of initial expanded state
  useEffect(() => {
    if (onToggleExpanded) {
      onToggleExpanded(expanded);
    }
  }, []);

  return (
    <nav className={`${styles.sidebar} ${expanded ? styles.expanded : ''}`}>
      <button 
        className={styles.toggleButton}
        onClick={toggleExpanded}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
        type="button"
      >
        <div className={styles.hamburger}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>
      
      <div className={styles.navItems}>
        <div className={expanded ? styles.expandedNavItemContainer : styles.navItemContainer}>
          <button 
            className={`${styles.navItem} ${currentPath === '/explore' ? styles.selected : ''}`}
            onClick={() => navigate('/explore')}
            aria-label="Home"
            type="button"
          >
            <img src={homeIcon} alt="Home" />
            {expanded && <span className={styles.navText}>Home</span>}
          </button>
        </div>
        
        <div className={expanded ? styles.expandedNavItemContainer : styles.navItemContainer}>
          <button 
            className={`${styles.navItem} ${currentPath === '/chats' ? styles.selected : ''}`}
            onClick={() => navigate('/chats')}
            aria-label="Chats"
            type="button"
          >
            <img src={chatsIcon} alt="Chats" />
            {expanded && <span className={styles.navText}>Chats</span>}
          </button>
        </div>
        
        <div className={expanded ? styles.expandedNavItemContainer : styles.navItemContainer}>
          <button 
            className={`${styles.navItem} ${currentPath === '/create' || currentPath === '/create-character' || currentPath === '/create-scenario' ? styles.selected : ''}`}
            onClick={() => navigate('/create')}
            aria-label="Create"
            type="button"
          >
            <img src={createIcon} alt="Create" className={styles.createIcon} />
            {expanded && <span className={styles.navText}>Create</span>}
          </button>
        </div>
        
        <div className={expanded ? styles.expandedNavItemContainer : styles.navItemContainer}>
          <button 
            className={`${styles.navItem}`}
            onClick={handleDiscordClick}
            aria-label="Join Discord"
            type="button"
          >
            <img src={discordIcon} alt="Discord" className={styles.discordIcon} />
            {expanded && <span className={styles.navText}>Join Discord</span>}
          </button>
        </div>
        
        <div className={expanded ? styles.expandedNavItemContainer : styles.navItemContainer}>
          <button 
            className={`${styles.navItem} ${currentPath.startsWith('/users/') ? styles.selected : ''}`}
            onClick={handleProfileClick}
            aria-label={isLoggedIn ? "Profile" : "Login"}
            type="button"
          >
            {!isLoading && (
              isLoggedIn ? (
                user?.icon ? (
                  <img 
                    src={user.icon} 
                    alt="Profile" 
                    className={styles.profileIcon}
                  />
                ) : (
                  <FiUser size={24} className={styles.profileIconSvg} />
                )
              ) : (
                <FiLogIn size={24} />
              )
            )}
            {expanded && <span className={styles.navText}>{isLoggedIn ? "Profile" : "Login"}</span>}
          </button>
        </div>
      </div>
      
      <div className={styles.profileSection}>
      </div>
    </nav>
  );
};

export default NavSidebar; 